import { useSearchParams } from 'react-router-dom'

import { SignedOutForm } from 'components/authentication/SignedOutForm'
import SignedOutFormContainer from 'components/authentication/SignedOutFormContainer'
import { Alert, AlertContent, AlertText } from 'components/common/Alert'
import Button from 'components/common/Button'
import Text from 'components/common/Text'
import { useConfirmOAuthConsent } from 'queries/oauth/use-confirm-oauth-consent'
import { useGetOAuthConsentDetails } from 'queries/oauth/use-get-oauth-consent-details'
import theme from 'theme'

import type { ReactNode } from 'react'
import type { OAuthConsentDetails } from 'types/api/oauth/consent'

export const REQUEST_ID_PARAM = 'request_id'

/** The document host when the client described itself; the name only when we chose it. */
function clientLabel(details: OAuthConsentDetails): string {
  return details.client.displayHost ?? details.client.displayName
}

/** Scope strings the API may grant, rendered as something a human can consent to. */
const SCOPE_DESCRIPTIONS: Record<string, string> = {
  'gremlin:full':
    'Use Gremlin on your behalf, limited to what your own account can already do',
  offline_access: 'Stay connected without asking you to authorize it again'
}

/**
 * Maps scope identifiers to consent copy, falling back to the raw identifier. The API can grant a
 * scope before this map knows about it, and showing the identifier is better than asking the user
 * to approve something invisible.
 */
function describeScopes(scopes: string[]): string[] {
  return scopes.map((value) => SCOPE_DESCRIPTIONS[value] ?? value)
}

function ScopeList({ details }: { details: OAuthConsentDetails }) {
  const scopes = describeScopes(details.scopes)

  if (scopes.length === 0) {
    return null
  }

  return (
    <ul
      css={{
        listStyle: 'none',
        margin: 0,
        marginBottom: theme.space[4],
        padding: 0,
        textAlign: 'left'
      }}
    >
      {scopes.map((description) => (
        <li key={description} css={{ marginBottom: theme.space[2] }}>
          <Text as="span">{description}</Text>
        </li>
      ))}
    </ul>
  )
}

/** Terminal states: something to read, and no decision to make. */
function ConsentMessage({
  children,
  title
}: {
  children: ReactNode
  title: string
}) {
  return (
    <SignedOutFormContainer>
      <SignedOutForm title={title} onSubmit={(e) => e.preventDefault()}>
        <Text as="div">{children}</Text>
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}

/**
 * The OAuth 2.0 consent screen.
 *
 * A route rather than a modal because the user arrives by a top-level browser redirect from
 * `/oauth2/authorize`, may bounce through login, and may leave our origin entirely for a corporate
 * IdP. A URL carrying `request_id` survives all three; in-memory modal state does not.
 *
 * Authentication is not handled here: an unauthenticated visitor is sent to login by
 * `UnauthenticatedRoutes` and returns afterwards, which is why this screen need not know an IdP
 * exists.
 */
export function OAuthConsent() {
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get(REQUEST_ID_PARAM) ?? ''

  const {
    data: oauthConsentDetails,
    isLoading,
    isError
  } = useGetOAuthConsentDetails(requestId, { enabled: Boolean(requestId) })

  const { mutate: mutateConfirmOAuthConsent, isLoading: deciding } =
    useConfirmOAuthConsent(requestId, {
      onSuccess: (result) => {
        // Leaving our origin for the client's callback, so a router navigate is wrong here.
        window.location.assign(result.redirectTo)
      }
    })

  if (!requestId) {
    return (
      <ConsentMessage title="Authorization request missing">
        This link is incomplete. Start the connection again from the application
        you were using.
      </ConsentMessage>
    )
  }

  if (isLoading) {
    return (
      <ConsentMessage title="Authorize application">Loading…</ConsentMessage>
    )
  }

  // Expired, already used, or never existed. The API deliberately does not distinguish these, so
  // neither do we.
  if (isError || !oauthConsentDetails) {
    return (
      <ConsentMessage title="Authorization request expired">
        This request is no longer valid. Start the connection again from the
        application you were using.
      </ConsentMessage>
    )
  }

  // An administrator has disabled AI and MCP access for the whole organization. No amount of user
  // consent overrides that, so offer no approve button at all.
  if (!oauthConsentDetails.llmAccessEnabled) {
    return (
      <ConsentMessage title="Access disabled">
        AI and MCP access is disabled for {oauthConsentDetails.companyName} by
        an administrator. Contact them if you need this connection.
      </ConsentMessage>
    )
  }

  return (
    <SignedOutFormContainer>
      <SignedOutForm
        title={`Connect ${clientLabel(oauthConsentDetails)}`}
        onSubmit={(e) => e.preventDefault()}
      >
        <Text as="p" css={{ marginBottom: theme.space[4] }}>
          <strong>{clientLabel(oauthConsentDetails)}</strong> is asking to
          access Gremlin as you, in{' '}
          <strong>{oauthConsentDetails.companyName}</strong>.
        </Text>

        {oauthConsentDetails.client.nameIsSelfAsserted && (
          <Text
            as="p"
            color="normal"
            css={{ marginBottom: theme.space[4], textAlign: 'left' }}
          >
            This application describes itself as &ldquo;
            {oauthConsentDetails.client.displayName}&rdquo;. That name is not
            verified by Gremlin &mdash; only the address above is.
          </Text>
        )}

        <ScopeList details={oauthConsentDetails} />

        {oauthConsentDetails.redirectHost && (
          <Text
            as="p"
            color="normal"
            css={{ marginBottom: theme.space[4], textAlign: 'left' }}
          >
            After you approve, Gremlin sends the authorization to{' '}
            <strong>{oauthConsentDetails.redirectHost}</strong>.
          </Text>
        )}

        {oauthConsentDetails.loopbackOnly && (
          <Alert css={{ marginBottom: theme.space[4] }} variation="yellow">
            <AlertContent>
              <AlertText>
                This connection completes on this computer. Any program running
                here can accept it, so approve only if you just started this
                connection yourself.
              </AlertText>
            </AlertContent>
          </Alert>
        )}

        {!oauthConsentDetails.refreshOffered && (
          <Text
            as="p"
            color="normal"
            css={{ marginBottom: theme.space[4], textAlign: 'left' }}
          >
            {oauthConsentDetails.companyName} does not allow connections to
            renew themselves, so this one ends when your session does and you
            will be asked to approve it again.
          </Text>
        )}

        {oauthConsentDetails.previouslyApproved && (
          <Alert css={{ marginBottom: theme.space[4] }} variation="base">
            <AlertContent>
              <AlertText>
                You have connected {clientLabel(oauthConsentDetails)} before.
                Approving again replaces the previous connection.
              </AlertText>
            </AlertContent>
          </Alert>
        )}

        <Button
          primary
          stretch
          css={{ marginBottom: theme.space[2] }}
          disabled={deciding}
          loading={deciding}
          onClick={() => mutateConfirmOAuthConsent({ approved: true })}
        >
          Allow access
        </Button>
        <Button
          secondary
          stretch
          disabled={deciding}
          onClick={() => mutateConfirmOAuthConsent({ approved: false })}
        >
          Deny
        </Button>
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}
